import type React from 'react';
import window from '@atlas/tool/box/browser/window';
import { type Editor as TSlateEditor, Transforms as SlateTransforms } from 'slate';
import { ReactEditor } from 'slate-react';

export default function createNodeResizeHandler(
    editor: TSlateEditor,
    selector: string,
    options?: TNodeResizeHandlerOptions,
): React.MouseEventHandler<HTMLElement> {
    const minSize = options?.minSize ?? 60;
    const maxWidth = options?.maxWidth;
    const maxHeight = options?.maxHeight;
    const direction = options?.direction ?? 'right';
    const attributeName = options?.attributeName ?? 'width';

    const isHorizontal = direction === 'right' || direction === 'left';

    return function handleImageResize(event) {
        const resizeEl = event.target;
        if (!(resizeEl instanceof HTMLElement)) return;
        if (!resizeEl.classList.contains(selector)) return;

        const resizeNode = ReactEditor.toSlateNode(editor, resizeEl);
        if (!resizeNode) return;

        event.preventDefault();
        const resizeNodePath = ReactEditor.findPath(editor, resizeNode);

        const parentEl = resizeEl.parentElement as HTMLElement;
        const initialNodeWidth = parentEl.offsetWidth;
        const initialNodeHeight = parentEl.offsetHeight;
        const ratio = initialNodeHeight / initialNodeWidth;
        const initialCursorDistance = isHorizontal ? event.clientX : event.clientY;

        function change(value: number) {
            SlateTransforms.setNodes(editor, { [attributeName]: value }, { at: resizeNodePath });
        }

        function handleMove(event: MouseEvent) {
            let changeX = 0,
                changeY = 0;

            if (isHorizontal) {
                changeX = event.clientX - initialCursorDistance;
                if (direction === 'left') changeX *= -1;
                changeY = Math.round(changeX * ratio);
            } else {
                changeY = event.clientY - initialCursorDistance;
                changeX = Math.round(changeY / ratio);
            }

            const modifiedWidth = initialNodeWidth + changeX;
            const modifiedHeight = initialNodeHeight + changeY;
            const isWidthLessThanHeight = initialNodeWidth < initialNodeHeight;

            if (isWidthLessThanHeight) {
                if (modifiedWidth < minSize) {
                    if (attributeName === 'width') {
                        return change(minSize);
                    } else {
                        return change(Math.round(minSize * ratio));
                    }
                }
            } else {
                if (modifiedHeight < minSize) {
                    if (attributeName === 'width') {
                        return change(Math.round(minSize / ratio));
                    } else {
                        return change(minSize);
                    }
                }
            }

            if (maxHeight && (isWidthLessThanHeight || !maxWidth) && modifiedHeight > maxHeight) {
                if (attributeName === 'width') {
                    return change(Math.round(maxHeight / ratio));
                } else {
                    return change(maxHeight);
                }
            }

            if (maxWidth && (!isWidthLessThanHeight || !maxHeight) && modifiedWidth > maxWidth) {
                if (attributeName === 'width') {
                    return change(maxWidth);
                } else {
                    return change(Math.round(maxWidth * ratio));
                }
            }

            if (attributeName === 'width') {
                change(modifiedWidth);
            } else {
                change(modifiedHeight);
            }
        }

        window?.addEventListener('mousemove', handleMove);
        window?.addEventListener('mouseup', () => window?.removeEventListener('mousemove', handleMove), { once: true });
    };
}

// #region Types

export type TNodeResizeHandlerOptions = {
    minSize?: number;
    maxHeight?: number;
    maxWidth?: number;
    direction?: 'right' | 'left' | 'bottom';
    attributeName?: 'width' | 'height';
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('createNodeResizeHandler', async () => {
        it.todo('🤷');
    });
}

// #endregion
